import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import CancelOutlinedIcon from '@mui/icons-material/Cancel';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import { isImage } from 'utils';
import useMediaUrl from 'hooks/useMediaURL';
import Spin from 'components/Spin';
import IconButton from 'components/IconButton';
import { BUCKET_URL } from 'constants/index';
import AudioPlayer from 'modules/phoneapp/components/AudioPlayer';

const Root = styled(Box)`
  border-radius: 4px;
  position: relative;
  img {
    border-radius: 3px;
    max-height: 55px;
    max-width: 100px;
    object-fit: contain;
  }
  .icon-remove {
    z-index: 1;
    position: absolute;
    right: -20px;
    top: -14px;

    svg {
      background: white;
      border-radius: 50%;
    }
  }
`;

const ModalContent = styled(Box)(() => ({
  margin: 'auto',
}));

const useStyle = makeStyles(() => ({
  paper: {
    backgroundColor: 'transparent',
  },
}));

const MessageAttachment = ({
  hash,
  url,
  mediaType = '',
  file,
  preview = true,
  onRemove,
  name,
}) => {
  const [t] = useTranslation('messages');
  const { url: awsUrl, loading } = useMediaUrl(hash);
  const [modalOpen, setModal] = useState(false);
  const [fileName, setFileName] = useState(name);
  const classes = useStyle();

  useEffect(() => {
    if (name) {
      setFileName(name);
    }
  }, [name]);

  useEffect(() => {
    if (!name && hash) {
      const name = hash.split('/').pop();
      setFileName(name);
    }
  }, [hash]);

  const toggleModal = (e) => {
    setModal(!modalOpen);
  };

  if (loading) return <Spin spinning />;
  else if (!awsUrl && !file && !url) return null;

  const removeElement = onRemove && (
    <IconButton
      color="error"
      className="icon-remove"
      title={t('remove-attachment', 'Remove attachment')}
      icon={<CancelOutlinedIcon />}
      onClick={onRemove}
      size="small"
    />
  );

  if (file) {
    return (
      <Root>
        <Box position="relative" display="inline-block">
          {isImage(file.file_name || file.name) ? (
            <img src={file.preview} alt="Attachment" />
          ) : (
            <Typography variant="caption">
              {file.file_name || file.name}
            </Typography>
          )}
          {removeElement}
        </Box>
      </Root>
    );
  } else if (
    ((awsUrl && isImage(awsUrl)) || (url && mediaType.includes('image'))) &&
    preview
  ) {
    const isVideo = /.+.webm\/.+/.test(hash);
    let imgUrl = url || awsUrl;
    let videoUrl;

    if (isVideo) {
      const [video, cover] = hash.split('/');
      imgUrl = `${BUCKET_URL}${cover}`;
      videoUrl = BUCKET_URL + video.replace('webm', 'mp4');
    }

    return (
      <Root>
        <Box display="inline-block" position="relative">
          <div onClick={toggleModal} style={{ cursor: 'pointer' }}>
            <img src={imgUrl} alt="Attachment" />
          </div>
          {removeElement}
        </Box>
        {modalOpen && (
          <Dialog
            open
            onClose={toggleModal}
            fullScreen
            classes={{ paperFullScreen: classes.paper }}
          >
            <ModalContent>
              <IconButton
                icon={<CancelOutlinedIcon />}
                onClick={toggleModal}
                sx={{ position: 'fixed', right: 10, top: 10, color: 'white' }}
                size="large"
              />
              {isVideo ? (
                <div>
                  <video
                    className="embed-responsive-item"
                    autoPlay={false}
                    preload="auto"
                    controls
                    poster={imgUrl}
                    width="100%"
                    src={videoUrl}
                  />
                </div>
              ) : (
                <img src={url || awsUrl} />
              )}
            </ModalContent>
          </Dialog>
        )}
      </Root>
    );
  } else if (awsUrl && (awsUrl.includes('.mp3') || awsUrl.includes('.m4a'))) {
    return (
      <Root>
        <AudioPlayer url={awsUrl} />
        {removeElement}
      </Root>
    );
  }
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="row" spacing={1} alignItems="center">
        <Link href={awsUrl || url} target="_blank" overflow="hidden">
          <CloudDownloadOutlinedIcon fontSize="small" />
        </Link>
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {fileName || hash}
        </span>
      </Stack>
      {removeElement}
    </Stack>
  );
};

export default MessageAttachment;
